import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { BACKEND_URL } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Modal from '../../components/ui/Modal';
import { Eye, CheckCircle, XCircle, Download, Copy, ExternalLink } from 'lucide-react';

const statusFilters = [
  { value: '', label: 'All Status' },
  { value: 'PENDING_PAYMENT', label: 'Pending Payment' },
  { value: 'PAYMENT_PROOF_SUBMITTED', label: 'Proof Submitted' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export default function AdminDepositsPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<any>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [copied, setCopied] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['adminDeposits', statusFilter, page],
    queryFn: () => api.get('/admin/deposits', { params: { status: statusFilter, page, limit: 20 } }).then(r => r.data),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/admin/topups/${id}/approve`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['adminDeposits'] }); setSelected(null); },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.patch(`/admin/topups/${id}/reject`, { reason }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['adminDeposits'] }); setSelected(null); setShowReject(false); setRejectReason(''); },
  });

  const deposits = data?.data || [];
  const meta = data?.meta || { total: 0, page: 1, totalPages: 1 };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING_PAYMENT': return 'text-amber-400 bg-amber-500/10 border border-amber-500/20';
      case 'PAYMENT_PROOF_SUBMITTED': return 'text-blue-400 bg-blue-500/10 border border-blue-500/20';
      case 'APPROVED': return 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20';
      case 'REJECTED': return 'text-red-400 bg-red-500/10 border border-red-500/20';
      case 'CANCELLED': return 'text-slate-400 bg-slate-500/10 border border-slate-500/20';
      default: return 'text-slate-400 bg-slate-500/10 border border-slate-500/20';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Deposit Management</h1>
          <p className="text-sm text-slate-400">{meta.total} total deposits</p>
        </div>
        <Button variant="secondary" size="sm"><Download className="w-4 h-4 mr-1" />Export</Button>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Pending Payment', count: deposits.filter((d: any) => d.status === 'PENDING_PAYMENT').length, color: 'text-amber-400 bg-amber-500/10 border border-amber-500/20' },
          { label: 'Proof Submitted', count: deposits.filter((d: any) => d.status === 'PAYMENT_PROOF_SUBMITTED').length, color: 'text-blue-400 bg-blue-500/10 border border-blue-500/20' },
          { label: 'Approved', count: data?.approvedCount || 0, color: 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20' },
          { label: 'Rejected', count: data?.rejectedCount || 0, color: 'text-red-400 bg-red-500/10 border border-red-500/20' },
        ].map((s, i) => (
          <div key={i} className={`rounded-lg border p-3 ${s.color}`}>
            <p className="text-xs font-medium opacity-75">{s.label}</p>
            <p className="text-2xl font-bold mt-1">{s.count}</p>
          </div>
        ))}
      </div>

      <Card>
        <CardContent className="py-3">
          <Select options={statusFilters} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06] bg-white/[0.02]">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">User</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Deposit Ref</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">UTR</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Proof</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {isLoading ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">Loading...</td></tr>
                ) : deposits.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">No deposits found</td></tr>
                ) : deposits.map((d: any) => (
                  <tr key={d.id} className="hover:bg-white/[0.02] cursor-pointer" onClick={() => { setSelected(d); setShowReject(false); setRejectReason(''); }}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-white">{d.user?.profile?.fullName || d.user?.username}</p>
                      <p className="text-xs text-slate-500">@{d.user?.username}</p>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-white">{formatCurrency(d.amount)}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-primary-400">{d.depositRef || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      {d.transactionId ? (
                        <span className="text-xs font-mono text-white">{d.transactionId}</span>
                      ) : <span className="text-xs text-slate-600">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {d.paymentProof ? (
                        d.paymentProof.startsWith('/uploads/') ? (
                          <a href={`${BACKEND_URL}${d.paymentProof}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                            className="text-primary-400 hover:text-primary-300 text-xs underline flex items-center gap-1">
                            View <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="text-xs text-slate-400 font-mono truncate max-w-[80px] block">{d.paymentProof}</span>
                        )
                      ) : <span className="text-xs text-slate-600">—</span>}
                    </td>
                    <td className="px-4 py-3"><Badge status={d.status} /></td>
                    <td className="px-4 py-3 text-sm text-slate-400">{new Date(d.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setSelected(d); setShowReject(false); }}><Eye className="w-3.5 h-3.5" /></Button>
                        {(d.status === 'PAYMENT_PROOF_SUBMITTED' || d.status === 'PENDING_PAYMENT') && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => approveMutation.mutate(d.id)}><CheckCircle className="w-3.5 h-3.5 text-emerald-400" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => { setSelected(d); setShowReject(true); }}><XCircle className="w-3.5 h-3.5 text-red-400" /></Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {meta.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
              <p className="text-sm text-slate-400">Page {meta.page} of {meta.totalPages}</p>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button variant="secondary" size="sm" disabled={page >= meta.totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={!!selected} onClose={() => { setSelected(null); setShowReject(false); }} title="Deposit Detail">
        {selected && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Amount</p><p className="font-bold text-lg text-white">{formatCurrency(selected.amount)}</p></div>
              <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Status</p><Badge status={selected.status} /></div>
              <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Method</p><p className="font-medium text-white capitalize">{selected.paymentMethod?.replace('_', ' ')}</p></div>
              <div className="p-3 bg-white/[0.02] rounded-lg"><p className="text-slate-400 text-xs">Date</p><p className="font-medium text-white">{new Date(selected.createdAt).toLocaleString()}</p></div>
            </div>

            <div className="p-3 bg-white/[0.02] rounded-lg">
              <p className="text-slate-400 text-xs mb-1">User</p>
              <p className="font-medium text-white">{selected.user?.profile?.fullName || selected.user?.username}</p>
              <p className="text-xs text-slate-500">@{selected.user?.username}</p>
            </div>

            {selected.depositRef && (
              <div className="p-3 bg-white/[0.02] rounded-lg flex items-center justify-between">
                <div><p className="text-slate-400 text-xs">Deposit Reference</p><p className="font-mono text-sm text-primary-400">{selected.depositRef}</p></div>
                <button onClick={() => handleCopy(selected.depositRef, 'ref')} className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white">
                  {copied === 'ref' ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            )}

            {selected.transactionId && (
              <div className="p-3 bg-white/[0.02] rounded-lg flex items-center justify-between">
                <div><p className="text-slate-400 text-xs">Transaction ID / UTR</p><p className="font-mono text-sm text-white">{selected.transactionId}</p></div>
                <button onClick={() => handleCopy(selected.transactionId, 'utr')} className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white">
                  {copied === 'utr' ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            )}

            {selected.paymentProof && (
              <div className="p-3 bg-white/[0.02] rounded-lg">
                <p className="text-slate-400 text-xs mb-2">Payment Proof</p>
                {selected.paymentProof.startsWith('/uploads/') ? (
                  <a href={`${BACKEND_URL}${selected.paymentProof}`} target="_blank" rel="noopener noreferrer">
                    <img src={`${BACKEND_URL}${selected.paymentProof}`} alt="Payment Proof"
                      className="max-w-full max-h-80 rounded-xl border border-white/10 object-contain cursor-pointer hover:border-primary-400/50" />
                  </a>
                ) : (
                  <div className="p-3 bg-white/[0.04] rounded-lg border border-white/10">
                    <p className="text-sm font-mono text-white">{selected.paymentProof}</p>
                  </div>
                )}
              </div>
            )}

            {!selected.paymentProof && !selected.transactionId && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <p className="text-xs text-amber-400">No payment proof submitted yet</p>
              </div>
            )}

            {selected.adminNote && (
              <div className="p-3 bg-white/[0.02] rounded-lg">
                <p className="text-slate-400 text-xs">Admin Note</p>
                <p className="text-sm text-white">{selected.adminNote}</p>
              </div>
            )}

            {(selected.status === 'PAYMENT_PROOF_SUBMITTED' || selected.status === 'PENDING_PAYMENT') && (
              <div className="flex gap-2 pt-3 border-t border-white/[0.06]">
                <Button onClick={() => approveMutation.mutate(selected.id)} isLoading={approveMutation.isPending}>
                  <CheckCircle className="w-4 h-4 mr-1" />Approve
                </Button>
                <Button variant="danger" onClick={() => setShowReject(true)}>
                  <XCircle className="w-4 h-4 mr-1" />Reject
                </Button>
              </div>
            )}

            {showReject && (
              <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-xl space-y-3">
                <label className="block text-sm font-medium text-white">Rejection Reason</label>
                <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter reason for rejection" rows={2}
                  className="w-full px-3 py-2 border border-white/[0.06] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white/[0.04] text-white placeholder-slate-500 resize-none" />
                <div className="flex gap-2">
                  <Button variant="danger" size="sm" onClick={() => rejectMutation.mutate({ id: selected.id, reason: rejectReason })} isLoading={rejectMutation.isPending}>
                    Confirm Reject
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => { setShowReject(false); setRejectReason(''); }}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
